import React, { useState, useEffect } from 'react';
import AuthContext from './authContext';
import { UserLoginDTO } from '../../Models/Login';
// lightweight JWT decode helper (avoids external dependency issues in some bundlers)
const decodeJwt = (token: string | null) : any => {
    if (!token) return null;
    try {
        const parts = token.split('.');
        if (parts.length < 2) return null;
        let payload = parts[1];
        // Replace URL-safe chars
        payload = payload.replace(/-/g, '+').replace(/_/g, '/');
        // Pad base64 string
        while (payload.length % 4) payload += '=';
        const decoded = typeof atob !== 'undefined' ? atob(payload) : Buffer.from(payload, 'base64').toString('binary');
        // decode utf-8
        try {
            return JSON.parse(decodeURIComponent(decoded.split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join('')));
        } catch {
            return JSON.parse(decoded);
        }
    } catch { return null; }
};
import ApiClient, { setTokenProvider } from '../Services/ApiClient';
import { useAlert } from '../components/ui/alertProvider';

const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
    const extractRoles = (decoded: any): string[] => {
        if (!decoded) return [];
        if (Array.isArray(decoded.roles)) return decoded.roles.map((r: any) => String(r));
        const primary = decoded.role ?? decoded.Role ?? null;
        if (primary) return Array.isArray(primary) ? primary.map((r: any) => String(r)) : [String(primary)];
        // fallback: search keys that contain 'role'
        for (const k of Object.keys(decoded)) {
            if (k.toLowerCase().includes('role')) {
                const v = decoded[k];
                if (Array.isArray(v)) return v.map((r: any) => String(r));
                if (typeof v === 'string') return [v];
            }
        }
        return [];
    };

    const [isAdmin, setIsAdmin] = useState<boolean>(() => {
        try {
            const t = localStorage.getItem('token');
            if (!t) return false;
            const decoded: any = decodeJwt(t);
            const roles = extractRoles(decoded);
            return roles.includes('Admin') || roles.includes('SuperAdmin');
        } catch { return false; }
    });
    const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(() => {
        try {
            const t = localStorage.getItem('token');
            if (!t) return false;
            const decoded: any = decodeJwt(t);
            const roles = extractRoles(decoded);
            return roles.includes('SuperAdmin');
        } catch { return false; }
    });
    const [userId, setUserId] = useState<number | null>(() => {
        try {
            const t = localStorage.getItem('token');
            if (!t) return null;
            const decoded: any = decodeJwt(t);
            const id = decoded?.id ?? decoded?.Id ?? decoded?.sub ?? null;
            if (!id) return null;
            const parsed = parseInt(String(id));
            return Number.isNaN(parsed) ? null : parsed;
        } catch { return null; }
    });
    const isLoggedIn = !!token;
    const { showAlert } = useAlert();
    const [isPatient, setIsPatient] = useState<boolean>(() => {
        try {
            const t = localStorage.getItem('token');
            if (!t) return false;
            const decoded: any = decodeJwt(t);
            const roles = extractRoles(decoded);
            return roles.includes('Patient');
        } catch { return false; }
    });

    const login = async (loginData: UserLoginDTO): Promise<boolean> => {
        try {
            const data: any = await ApiClient.post('/auth/login', loginData);
            const receivedToken = data?.token ?? data?.Token ?? null;
            if (!receivedToken) {
                showAlert('Login failed: No token received', 'error');
                return false;
            }
            setToken(receivedToken);
            localStorage.setItem('token', receivedToken);
            // Update ApiClient token provider to read from the stateful token
            try { setTokenProvider(() => receivedToken); } catch { }
                try {
                const decoded: any = decodeJwt(receivedToken);
                const roles = extractRoles(decoded);
                    setIsAdmin(roles.includes('Admin') || roles.includes('SuperAdmin'));
                    setIsSuperAdmin(roles.includes('SuperAdmin'));
                    setIsPatient(roles.includes('Patient'));
                    const id = decoded?.id ?? decoded?.Id ?? decoded?.sub ?? null;
                    const parsed = id ? parseInt(String(id)) : NaN;
                    setUserId(!Number.isNaN(parsed) ? parsed : null);
                    // expose siteId if present in token (string GUID). Try common claim names
                    const siteClaim = decoded?.siteId || decoded?.SiteId || decoded?.site || null;
                    if (siteClaim) {
                        try { (window as any).siteId = siteClaim; } catch {}
                    }
                } catch {
                    setIsAdmin(false);
                    setIsSuperAdmin(false);
                    setIsPatient(false);
                    setUserId(null);
                }
            window.dispatchEvent(new Event('login'));
            return true;
        } catch (error: any) {
            // ApiClient throws with error.data and error.message
            const message = error?.data?.message || error?.message || 'Login failed';
            showAlert(message, 'error');
            return false;
        }
    };

    // registration account type selected on login page (persist to localStorage for navigation)
    const [registrationAccountType, setRegistrationAccountTypeState] = React.useState<'patient' | 'manager' | null>(() => {
        try { const v = localStorage.getItem('registrationAccountType'); return v === 'manager' ? 'manager' : v === 'patient' ? 'patient' : null; } catch { return null; }
    });
    const setRegistrationAccountType = (t: 'patient' | 'manager' | null) => {
        try { if (t) localStorage.setItem('registrationAccountType', t); else localStorage.removeItem('registrationAccountType'); } catch {}
        setRegistrationAccountTypeState(t);
    };

    const logout = () => {
        setToken(null);
        setIsAdmin(false);
        setIsPatient(false);
        setUserId(null);
        localStorage.removeItem('token');
        try { setTokenProvider(() => null); } catch { }
        window.dispatchEvent(new Event('logout'));
    };

    const isTokenExpired = (tokenValue: string | null): boolean => {
        if (!tokenValue) return true;
        try {
            const decodedToken: any = decodeJwt(tokenValue);
            const currentTime = Date.now() / 1000;
            return decodedToken.exp < currentTime;
        } catch (error) {
            console.error('Error decoding token:', error);
            return true;
        }
    };

    useEffect(() => {
        const syncToken = () => setToken(localStorage.getItem('token'));
        window.addEventListener('storage', syncToken);
        return () => window.removeEventListener('storage', syncToken);
    }, []);

    // Expose siteId on auth object for consumers. Try decode from token on initial load as well.
    useEffect(() => {
        try {
            const t = token ?? localStorage.getItem('token');
            if (!t) return;
            const decoded: any = decodeJwt(t);
            const siteClaim = decoded?.siteId || decoded?.SiteId || decoded?.site || null;
            if (siteClaim) {
                (window as any).siteId = siteClaim;
            }
        } catch { }
    }, [token]);

    const authValue: any = { isLoggedIn, token, login, logout, isTokenExpired, isAdmin, isSuperAdmin, userId, registrationAccountType, setRegistrationAccountType };
    // attach siteId if available on window (set during login)
    try { authValue.siteId = (window as any).siteId ?? null; } catch { authValue.siteId = null; }
    try { authValue.isPatient = isPatient; } catch { }
    try { authValue.roles = (() => { const t = token ?? localStorage.getItem('token'); if (!t) return []; try { const d: any = decodeJwt(t); if (Array.isArray(d.roles)) return d.roles; const r = d?.role || d?.Role || null; return r ? [r] : []; } catch { return []; } })(); } catch { }

    return (
        <AuthContext.Provider value={authValue}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
